/**
 * Ordering for shipment view tabs.
 *
 * Position is stored as an `order` integer inside each tab record's config, since
 * the saved-views record has no field of its own to put it in. Records created
 * before ordering existed have no `order` at all, so every helper here has to
 * treat that as "unpositioned" rather than as 0 — otherwise old tabs would all
 * pile up at the front and swaps between them would silently do nothing.
 */

const UNPOSITIONED = Number.MAX_SAFE_INTEGER;

const orderOf = (tab) =>
  Number.isFinite(tab?.order) ? tab.order : UNPOSITIONED;

/** Unpositioned tabs sort last, then ties break on id so the order is stable. */
export const compareViewTabs = (a, b) => {
  const delta = orderOf(a) - orderOf(b);
  if (delta !== 0) return delta;
  return String(a?.id ?? "").localeCompare(String(b?.id ?? ""), undefined, {
    numeric: true,
  });
};

export const sortViewTabs = (tabs = []) => [...tabs].sort(compareViewTabs);

/**
 * Drop the tab `sourceId` onto the slot currently held by `targetId`.
 *
 * Move-to-index, not a swap: dragging a tab across several others shifts them all
 * by one rather than exchanging two.
 *
 * The source lands *on* the target's slot. The column manager's `reorderIdsInArray`
 * instead always inserts before the target, which makes dragging an item onto the
 * row directly below it a no-op — tolerable in a long column list where you aim at
 * a distant row, but the dominant gesture in a list of three or four tabs, where an
 * inert drag just reads as broken.
 *
 * Returns `null` when the move is a no-op — unknown tab, or dropped on itself — so
 * callers can skip the write entirely. Otherwise returns the fully renumbered list
 * plus just the records whose stored order actually changed.
 */
export const reorderViewTabs = (tabs = [], sourceId, targetId) => {
  const sorted = sortViewTabs(tabs);
  const sourceIndex = sorted.findIndex((t) => String(t.id) === String(sourceId));
  const targetIndex = sorted.findIndex((t) => String(t.id) === String(targetId));
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return null;

  const next = [...sorted];
  const [moved] = next.splice(sourceIndex, 1);
  // Inserting at the target's original index works in both directions: dragging
  // down, lifting the source has already shifted the target back one slot, so this
  // index is the one the target used to occupy.
  next.splice(targetIndex, 0, moved);

  // Renumber the whole list: a swap of two unpositioned records changes no stored
  // value on its own, so without this the move wouldn't survive a reload. The
  // config copy is kept in step because that is what actually gets persisted.
  const renumbered = next.map((tab, i) => ({
    ...tab,
    order: i,
    config: { ...(tab.config || {}), order: i },
  }));

  const previousOrder = new Map(
    tabs.map((tab) => [String(tab.id), Number.isFinite(tab.order) ? tab.order : null])
  );
  const changed = renumbered.filter(
    (tab) => previousOrder.get(String(tab.id)) !== tab.order
  );

  return { tabs: renumbered, changed };
};

/** Order for a newly added tab: the end of the list. */
export const nextViewTabOrder = (tabs = []) =>
  tabs.reduce(
    (max, tab) => (Number.isFinite(tab?.order) ? Math.max(max, tab.order) : max),
    -1
  ) + 1;
